import {
  Button,
  Field,
  Modal,
  ModalClose,
  ModalContent,
  Stack,
  TextInput,
  toast,
} from '@reelplicate/ui';
import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { getErrorMessage } from '@/lib/errors';

import { useSavePost } from './use-save-post';

/**
 * "Add from link": paste a competitor's TikTok or Instagram reel or carousel
 * and it's saved to the library with its numbers and what it says (spoken
 * or on its slides). Fetching runs on the server; the dialog waits for it,
 * then opens the saved post.
 */
export function ImportLinkModal({
  workspaceId,
  open,
  collectionId,
  onOpenChange,
}: {
  workspaceId: string;
  open: boolean;
  /** Save straight into this hand-picked collection. */
  collectionId?: string;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      {open && (
        <ImportForm
          workspaceId={workspaceId}
          collectionId={collectionId}
          onDone={() => onOpenChange(false)}
        />
      )}
    </Modal>
  );
}

function ImportForm({
  workspaceId,
  collectionId,
  onDone,
}: {
  workspaceId: string;
  collectionId?: string;
  onDone: () => void;
}) {
  const formId = useId();
  const navigate = useNavigate();
  const { save: savePost } = useSavePost(workspaceId);
  const [url, setUrl] = useState('');
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Closing the dialog stops the waiting; the save carries on on the server.
  const closed = useRef(false);
  useEffect(
    () => () => {
      closed.current = true;
    },
    [],
  );

  const save = async () => {
    const link = url.trim();
    if (!link || waiting) return;
    setError(null);
    setWaiting(true);
    try {
      const progress = await savePost(link, { collectionId, stop: () => closed.current });
      if (!progress || closed.current) return;
      if (progress.status === 'FAILED') {
        setError(progress.error ?? 'That post couldn’t be saved. Try again.');
        return;
      }
      toast.success(
        progress.alreadySaved ? 'Already in your library' : 'Saved to your library',
      );
      onDone();
      if (progress.asset) void navigate(`/media?asset=${progress.asset.id}`);
    } catch (failure) {
      if (!closed.current) setError(getErrorMessage(failure));
    } finally {
      if (!closed.current) setWaiting(false);
    }
  };

  return (
    <ModalContent
      title="Add from link"
      description="Paste a TikTok or Instagram reel or carousel. We save its views, engagement and what it says, so you can make your version."
      footer={
        <>
          <ModalClose asChild>
            <Button variant="ghost">Cancel</Button>
          </ModalClose>
          <Button type="submit" form={formId} loading={waiting} disabled={!url.trim()}>
            {waiting ? 'Fetching post…' : 'Save'}
          </Button>
        </>
      }
    >
      <Stack
        as="form"
        id={formId}
        gap={4}
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <Field
          label="Post link"
          error={error ?? undefined}
          helper={
            error
              ? undefined
              : 'Share → Copy link in the app works, short links included.'
          }
        >
          <TextInput
            type="url"
            value={url}
            autoComplete="off"
            placeholder="https://www.tiktok.com/@brand/video/…"
            disabled={waiting}
            onChange={(event) => {
              setUrl(event.target.value);
              setError(null);
            }}
          />
        </Field>
      </Stack>
    </ModalContent>
  );
}
